import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Flag } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const REASON_KEYS = ["report_reason_spam", "report_reason_inappropriate", "report_reason_duplicate", "report_reason_wrong", "report_reason_other"];

export const ReportButton = ({ targetType, targetSlug, className = "" }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [tags, setTags] = useState([]);
  const [sending, setSending] = useState(false);

  const toggleTag = (key) => setTags((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const submit = async () => {
    setSending(true);
    try {
      await api.post("/reports", { target_type: targetType, target_slug: targetSlug, reason: reason.trim(), reason_tags: tags });
      toast.success(t("report_sent"));
      setOpen(false);
      setReason("");
      setTags([]);
    } catch (e) {
      toast.error(e?.response?.status === 401 ? t("report_login") : (e?.response?.data?.detail || "Error"));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button data-testid="report-btn" onClick={() => setOpen(true)} className={`inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-red-400 transition-colors ${className}`}>
        <Flag className="w-3.5 h-3.5" /> {t("report_action")}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="report-dialog" className="z-[1200] bg-[#121418] border-[#272A34] text-stone-200 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-amber-100 flex items-center gap-2">
              <Flag className="w-5 h-5 text-red-400" /> {t("report_title")}
            </DialogTitle>
            <DialogDescription className="text-stone-400">{t("report_prompt")}</DialogDescription>
          </DialogHeader>

          <div data-testid="report-reasons" className="flex flex-wrap gap-2">
            {REASON_KEYS.map((key) => {
              const active = tags.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  data-testid={`report-reason-${key}`}
                  onClick={() => toggleTag(key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${active ? "bg-red-900/50 border-red-500/50 text-red-200" : "bg-[#181A20] border-[#272A34] text-stone-400 hover:border-red-500/30"}`}
                >
                  {t(key)}
                </button>
              );
            })}
          </div>

          <Textarea
            data-testid="report-reason-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("report_placeholder")}
            maxLength={1000}
            rows={4}
            className="bg-[#181A20] border-[#272A34] text-stone-200 placeholder:text-stone-600 focus-visible:ring-amber-500/40"
          />
          <DialogFooter className="gap-2 sm:gap-2">
            <Button data-testid="report-cancel-btn" variant="ghost" onClick={() => setOpen(false)} className="text-stone-400 hover:text-stone-200 hover:bg-[#181A20]">{t("report_cancel")}</Button>
            <Button data-testid="report-submit-btn" onClick={submit} disabled={sending || (tags.length === 0 && !reason.trim())} className="bg-gradient-to-r from-red-700 to-red-800 hover:from-red-600 hover:to-red-700 text-white border border-red-500/30">
              {sending ? "…" : t("report_submit")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReportButton;
